import isArray from 'lodash/isArray';
import isFunction from 'lodash/isFunction';
import isObject from 'lodash/isObject';
import PropTypes from 'prop-types';
import React from 'react';
import Grid from '../grid';
import ModalModule from '../modal';
import UI from '../ui';
import Utils from '../utils';
import DeleteTagModal from './DeleteTagModal.component';
import EditTagModal from './EditTagModal.component';

const { Communication } = Utils;
const { GridPanel } = Grid;
const { Spinner, WrappedToolbar } = UI;
const { Modal, ModalContext } = ModalModule;

const ColorCellRenderer = ({ data: { Color = '' } }) => (
	<div
		className="color-bar"
		style={{
			background: Color,
		}}
	/>
);

export default class ManageTagsModal extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			columnDefs: [
				{
					headerName: 'Name',
					field: 'Tag',
					sortable: false,
				},
				{
					headerName: 'Color',
					field: 'Color',
					flex: 1,
					sortable: false,
					cellClass: 'color-cell',
					cellRenderer: 'ColorCellRenderer',
				},
			],
			frameworkComponents: {
				ColorCellRenderer,
			},
			isLoading: true,
			selectTag: {},
			tagOptions: [],
		};
	}

	componentDidMount = () => {
		this.getTags();
	};

	/**
	 * Call addTag endpoint to add a new tag
	 * @param {object} contains tag's name and color
	 */
	addTag = async ({ tagName, tagColor }) => {
		const { showError } = this.context;

		const response = await Communication.addTag({
			Color: tagColor,
			SellerID: null,
			Tag: tagName,
		});

		if (!Communication.isSuccessful(response)) {
			return showError({ response });
		}

		this.onUpdateSuccessful();
	};

	/**
	 * Call deleteTag endpoint to delete the selected tag
	 */
	deleteTag = async () => {
		const { showError } = this.context;
		const { selectTag } = this.state;
		const response = await Communication.deleteTag(selectTag.OrderTagID);

		if (!Communication.isSuccessful(response)) {
			return showError({ response });
		}

		this.onUpdateSuccessful();
	};

	/**
	 * Call updateTag endpoint to update the selected tag
	 * @param {object} contains tag's name and color
	 */
	editTag = async ({ tagName, tagColor }) => {
		const { showError } = this.context;
		const { selectTag } = this.state;

		if (selectTag.Tag === tagName && selectTag.Color === tagColor) {
			return;
		}

		const response = await Communication.updateTag({
			Color: tagColor,
			OrderTagID: selectTag.OrderTagID,
			SellerID: selectTag.SellerID,
			Tag: tagName,
		});

		if (!Communication.isSuccessful(response)) {
			return showError({ response });
		}

		this.onUpdateSuccessful();
	};

	/**
	 * call the endpoint to get the tag options
	 * @return {array}
	 */
	getTags = async callback => {
		const { showError } = this.context;
		const response = await Communication.getTags();
		const tagOptions =
			isObject(response.data) && isArray(response.data.tags)
				? response.data.tags
				: [];

		if (!Communication.isSuccessful(response)) {
			showError({ response });
		}

		this.setState({
			tagOptions,
			isLoading: false,
		});

		if (callback) {
			callback(tagOptions);
		}
	};

	/**
	 * show a modal for users to add a new tag
	 */
	onClickAddButton = () => {
		const { showModal } = this.context;
		const { tagOptions } = this.state;

		showModal({
			component: EditTagModal,
			data: {
				tagOptions,
				type: 'Add',
				onSaveTagCallback: this.addTag,
			},
		});
	};

	/**
	 * show a modal for users to edit the selected tag
	 */
	onClickEditButton = () => {
		const { showModal } = this.context;
		const { selectTag, tagOptions } = this.state;

		showModal({
			component: EditTagModal,
			data: {
				selectTag,
				tagOptions,
				type: 'Edit',
				onSaveTagCallback: this.editTag,
			},
		});
	};

	/**
	 * show a confirmation modal before users delete the selected tag
	 */
	onClickDeleteButton = () => {
		const { showModal } = this.context;
		const { selectTag } = this.state;

		showModal({
			component: DeleteTagModal,
			data: {
				name: selectTag.Tag,
				onDeleteCallback: this.deleteTag,
			},
		});
	};

	/**
	 * tag selection changes triggered by the grid
	 * @param {array} selection the selected tag
	 */
	onSelectionChanged = selection => {
		this.setState({
			selectTag: selection.length === 1 ? selection[0].data : {},
		});
	};

	/**
	 * update tag options when users add/edit/delete any tags
	 */
	onUpdateSuccessful = async () => {
		const {
			data: { onUpdateTagOptionsCallback },
		} = this.props;

		this.setState({
			isLoading: true,
		});

		this.getTags(tagOptions => {
			// update tagOptions if onUpdateTagOptionsCallback callback is provided
			if (isFunction(onUpdateTagOptionsCallback)) {
				onUpdateTagOptionsCallback(tagOptions);
			}
		});
	};

	render() {
		const { modalId } = this.props;
		const {
			columnDefs,
			frameworkComponents,
			isLoading,
			selectTag,
			tagOptions,
		} = this.state;
		const { OrderTagID } = selectTag;
		const buttons = [
			{
				label: 'Add',
				icon: 'add-icon',
				dataqa: 'add-new-tag',
				disabled: isLoading,
				onClick: this.onClickAddButton,
			},
			{
				label: 'Edit',
				dataqa: 'edit-tag',
				disabled: isLoading || !OrderTagID,
				onClick: this.onClickEditButton,
			},
			{
				label: 'Delete',
				dataqa: 'delete-tag',
				disabled: isLoading || !OrderTagID,
				onClick: this.onClickDeleteButton,
			},
		];

		return (
			<Modal
				modalId={modalId}
				className="common-tags manage-tags-modal"
				header="Manage Tags"
				size="small"
				hideFooter
			>
				<WrappedToolbar buttons={buttons} dataqa="manage-tags-toolbar" />
				<GridPanel
					className="manage-tags-grid"
					columnDefs={columnDefs}
					domLayout="autoHeight"
					frameworkComponents={frameworkComponents}
					rowData={tagOptions}
					showPagination={false}
					rowSelection="single"
					dataqa="manage-tags-grid"
					onSelectionChanged={this.onSelectionChanged}
				/>
				{isLoading ? <Spinner msg="Updating Tags" /> : null}
			</Modal>
		);
	}
}
ManageTagsModal.contextType = ModalContext;

ManageTagsModal.propTypes = {
	// callback to update caller's tag options
	onUpdateTagOptionsCallback: PropTypes.func,
};
